import { add, max, min } from "@pentacode/openapi";
import { ShiftProgress, ShiftPublishStatus } from "./api/rpc/shift.dto";
import { getAutomaticBreak } from "./breaks";
import { GLOBAL_CONSTANTS } from "./global-constants";
import { BreakMode, MealsMode, TimeEntryType } from "./model";
import { getGap, overlap } from "./time/interval";
import { addTimeResults, getTotalResults } from "./time/time-result";
import { getWeekNumber, getWeekYear, parseDateString, parseTimes } from "./utils";
export * from "./time/balances";
export * from "./time/break-period.util";
export * from "./time/consts";
export * from "./time/daily-results";
export * from "./time/date-range";
export * from "./time/interval";
export * from "./time/nominals";
export * from "./time/time-result";
/**
 * Mutates the given date by applying rounding to the minutes.
 *
 * If `rounding` is positive, the date is rounded up, if negative, it is rounded down.
 *
 * The date is also rounded down to the full minute.
 *
 * @param date The date to apply rounding to
 * @param rounding The rounding to apply in Minutes
 */
export function applyRounding(date, rounding) {
    if (rounding) {
        const minutes = date.getMinutes();
        // Minutes subtracted to round down
        const deltaRoundDown = minutes % rounding;
        // If rounding is positive, rounding is added to round up
        const deltaRoundUp = Math.max(0, rounding);
        // apply roundings
        date.setMinutes(minutes - deltaRoundDown + deltaRoundUp);
    }
    // Round to full minute
    date.setSeconds(0);
    date.setMilliseconds(0);
}
/**
 * Corrects to `plannedDate` if `date` was logged before `plannedDate` but rounded up beyond it
 * or if it was logged after plannedDate, but rounded down to earlier than it
 * @param date The date to be corrected
 * @param loggedTime The date `date` was logged
 * @param plannedDate The date that `date` should be corrected to
 * @returns Copy of `plannedDate` if correction is neccessary, otherwise `date`
 */
export function correctRoundedDate(date, loggedTime, plannedDate) {
    if (!plannedDate)
        return date;
    if ((loggedTime <= plannedDate && date > plannedDate) || (loggedTime >= plannedDate && date < plannedDate)) {
        return new Date(plannedDate);
    }
    return date;
}
export function getStartFinal(timeEntry, loggedTime, useActualTimeAtShiftStart, shiftStartRounding) {
    // If logged time is earlier than planned and useActualTimeAtShiftStart setting
    // is not set, use planned time instead
    let startFinal = timeEntry.startPlanned && loggedTime < timeEntry.startPlanned && !useActualTimeAtShiftStart
        ? new Date(timeEntry.startPlanned)
        : new Date(loggedTime);
    applyRounding(startFinal, shiftStartRounding);
    startFinal = correctRoundedDate(startFinal, loggedTime, timeEntry.startPlanned);
    return startFinal;
}
export function getEndFinal(timeEntry, loggedTime, useActualTimeAtShiftEnd, shiftEndRounding) {
    // If logged time is later than planned and useActualTimeAtShiftEnd setting
    // is not set, use planned time instead
    let endFinal = timeEntry.endPlanned && loggedTime > timeEntry.endPlanned && !useActualTimeAtShiftEnd
        ? new Date(timeEntry.endPlanned)
        : new Date(loggedTime);
    applyRounding(endFinal, shiftEndRounding);
    endFinal = correctRoundedDate(endFinal, loggedTime, timeEntry.endPlanned);
    // Make sure end time is not earlier than start time
    if (timeEntry.startFinal && endFinal < timeEntry.startFinal) {
        endFinal = timeEntry.startFinal;
    }
    return endFinal;
}
export function applyAutoMeals(company, timeEntry, { breakfast, lunch, dinner }) {
    const timeSettings = company.getTimeSettings({ timeEntry });
    if (timeSettings.mealsMode !== MealsMode.Auto ||
        !timeEntry.final ||
        timeEntry.durationFinal < timeSettings.mealsMinDuration) {
        return;
    }
    const breakfastInterval = timeSettings.mealEnabledBreakfast &&
        timeSettings.mealStartBreakfast &&
        timeSettings.mealEndBreakfast &&
        parseTimes(timeEntry.date, timeSettings.mealStartBreakfast, timeSettings.mealEndBreakfast);
    const lunchInterval = timeSettings.mealEnabledLunch &&
        timeSettings.mealStartLunch &&
        timeSettings.mealEndLunch &&
        parseTimes(timeEntry.date, timeSettings.mealStartLunch, timeSettings.mealEndLunch);
    const dinnerInterval = timeSettings.mealEnabledDinner &&
        timeSettings.mealStartDinner &&
        timeSettings.mealEndDinner &&
        parseTimes(timeEntry.date, timeSettings.mealStartDinner, timeSettings.mealEndDinner);
    if (breakfastInterval && breakfast && overlap(timeEntry.final, [breakfastInterval])) {
        timeEntry.mealsBreakfast = 1;
    }
    if (dinnerInterval && dinner && overlap(timeEntry.final, [dinnerInterval])) {
        timeEntry.mealsDinner = 1;
    }
    if (lunchInterval && lunch && overlap(timeEntry.final, [lunchInterval])) {
        timeEntry.mealsLunch = 1;
    }
}
export function applyAutoBreaks(entry, breakMode, autoBreaks) {
    const breakAuto = [BreakMode.Planned, BreakMode.PlannedPlusManual, BreakMode.PlannedOrManual].includes(breakMode)
        ? entry.breakPlanned || 0
        : getAutomaticBreak(entry.durationFinal, { autoBreaks, breakMode });
    const prevBreak = entry.break || 0;
    const breakLogged = entry.breakLogged || 0;
    if (entry.break === null || breakAuto !== entry.breakAuto) {
        const prevBreakAuto = entry.breakAuto ?? breakAuto;
        entry.breakAuto = breakAuto;
        const brk = [BreakMode.AutoOrManual, BreakMode.PlannedOrManual].includes(breakMode)
            ? max(breakAuto, breakLogged)
            : add(breakAuto, breakLogged);
        const prevBreakAutoWithManual = [BreakMode.AutoOrManual, BreakMode.PlannedOrManual].includes(breakMode)
            ? max(prevBreakAuto, breakLogged)
            : add(prevBreakAuto, breakLogged);
        // If break is null or hasn't been edited after the fact, update value
        if (entry.break === null || prevBreak - prevBreakAutoWithManual * 60 < 1) {
            entry.break = brk;
        }
    }
    // Checkout rounding or shortening the shift can leave it below the break; never yield negative work time
    if (entry.break !== null) {
        entry.break = min(entry.break, entry.durationFinal);
    }
}
export function getResultBreakDown(company, timeEntries) {
    timeEntries = timeEntries.filter((e) => e.type !== TimeEntryType.VacationAdjustment && Boolean(e.result));
    const breakdown = {
        items: [],
        total: getTotalResults(timeEntries.filter((e) => Boolean(e.result)).map((entry) => entry.result)),
    };
    const items = new Map();
    for (const { type, positionId, result, comment } of timeEntries) {
        if (!result) {
            continue;
        }
        const key = [TimeEntryType.HourAdjustment, TimeEntryType.VacationAdjustment].includes(type)
            ? `${type}_${positionId}_${comment}`
            : `${type}_${positionId}`;
        const existing = items.get(key);
        if (existing) {
            existing.result = addTimeResults(existing.result, result);
        }
        else {
            const workArea = (positionId && company.getPosition(positionId)) || null;
            items.set(key, {
                type,
                result,
                workArea,
                comment: [TimeEntryType.HourAdjustment, TimeEntryType.VacationAdjustment].includes(type)
                    ? comment
                    : undefined,
            });
        }
    }
    breakdown.items = [...items.values()];
    return breakdown;
}
/**
 * Returns the entry's planned time range, deriving the missing side from a
 * 10h assumption when only one of `startPlanned` / `endPlanned` is set. Returns
 * null only when both sides are missing.
 */
export function getPlannedOrAssumedRange(entry) {
    if (entry.startPlanned && entry.endPlanned)
        return [entry.startPlanned, entry.endPlanned];
    if (entry.startPlanned)
        return [entry.startPlanned, new Date(entry.startPlanned.getTime() + GLOBAL_CONSTANTS.ASSUMED_SHIFT_MS)];
    if (entry.endPlanned)
        return [new Date(entry.endPlanned.getTime() - GLOBAL_CONSTANTS.ASSUMED_SHIFT_MS), entry.endPlanned];
    return null;
}
export function rangeDurationHours(range) {
    return (range[1].getTime() - range[0].getTime()) / 3600 / 1000;
}
/**
 * Returns the first rule `entry` would violate against `otherEntries`, or null
 * if none. Checks are ordered: same-day overlap → 11h rest (ArbZG §5) → 10h
 * daily total planned-hours cap.
 *
 * Only Work entries belonging to `employeeId` in `otherEntries` are considered;
 * deleted entries are ignored.
 *
 * Shifts with only one planned side (start XOR end) are treated as 10h shifts
 * for all three checks, anchored at the known side. An entry with neither side
 * planned cannot be scheduled — returns `{ kind: "overlap" }` as the catch-all
 * (callers that need to distinguish "no range" from a real overlap should
 * pre-check with `getPlannedOrAssumedRange`).
 */
export function findShiftConflict(entry, employeeId, otherEntries) {
    const employeeEntries = otherEntries.filter((e) => !e.deleted && e.employeeId === employeeId && e.type === TimeEntryType.Work);
    const entryRange = getPlannedOrAssumedRange(entry);
    if (!entryRange)
        return "overlap";
    const hasOverlap = employeeEntries.some((e) => {
        const er = getPlannedOrAssumedRange(e);
        return er && overlap(entryRange, [er]) > 0;
    });
    if (hasOverlap)
        return "overlap";
    const hasRestViolation = employeeEntries.some((e) => {
        if (e.date === entry.date)
            return false;
        const ep = getPlannedOrAssumedRange(e);
        if (!ep)
            return false;
        const gap = getGap(ep, entryRange);
        return gap !== null && gap < GLOBAL_CONSTANTS.MIN_REST_MS;
    });
    if (hasRestViolation)
        return "rest";
    const sameDayEntries = employeeEntries.filter((e) => e.date === entry.date);
    const totalDuration = sameDayEntries.reduce((sum, e) => {
        const er = getPlannedOrAssumedRange(e);
        return sum + (er ? rangeDurationHours(er) : 0);
    }, 0) + rangeDurationHours(entryRange);
    // @TODO: move 10h cap to global constant or use localized value
    if (totalDuration > 10)
        return "cap";
    return null;
}
/**
 * Returns true when `entry` can be added to the given employee's roster without
 * violating any of: same-day overlap, the 11h rest period between different-day
 * shifts (ArbZG §5), or the 10h daily total planned-hours cap. Thin alias over
 * {@link findShiftConflict}.
 */
export function hasNoShiftConflict(entry, employeeId, otherEntries) {
    return findShiftConflict(entry, employeeId, otherEntries) === null;
}
/**
 * Returns the employee's most common workdays as an array of weekday sunday-based indices.
 *
 * Example: Tue–Fri yields `[2, 3, 4, 5]`.
 */
export function getCommonWorkDays(days) {
    const daysPerWeekDay = [0, 0, 0, 0, 0, 0, 0];
    const daysPerWeek = new Map();
    for (const day of days) {
        const date = parseDateString(day.date);
        if (!date)
            continue;
        const weekDay = date.getDay();
        daysPerWeekDay[weekDay]++;
        const week = `${getWeekYear(date)},${getWeekNumber(date)}`;
        daysPerWeek.set(week, (daysPerWeek.get(week) || 0) + 1);
    }
    const daysPerWeekArr = Array.from(daysPerWeek.values());
    const totaldays = daysPerWeekArr.reduce((a, b) => a + b, 0);
    const weeksWorked = daysPerWeekArr.filter((val) => val > 0).length;
    const averageDaysPerWeek = Math.round(totaldays / weeksWorked);
    const commonWorkDays = daysPerWeekDay
        .map((count, i) => [count, i])
        .sort(([a], [b]) => b - a)
        .slice(0, averageDaysPerWeek)
        .map(([, i]) => i);
    return commonWorkDays;
}
/**
 * Collapses a Work entry's publish snapshot and final times into the single
 * lifecycle status exposed by the shift API.
 */
export function shiftProgress(entry, now = new Date()) {
    if (entry.endFinal && entry.endFinal < now) {
        return ShiftProgress.Completed;
    }
    if (entry.startFinal && entry.startFinal < now) {
        return ShiftProgress.InProgress;
    }
    return ShiftProgress.Planned;
}
export function shiftPublishStatus(entry) {
    if (!entry.published) {
        return ShiftPublishStatus.Draft;
    }
    return entry.isPublished ? ShiftPublishStatus.Published : ShiftPublishStatus.ChangedSinceLastPublished;
}
//# sourceMappingURL=time.js.map